// Mock data for the Subscriptions Review screen: Barn supplements and
// Hydration groups. Each group matches the approved HTML selectSubscription()
// payload, with pricing sourced from the pricing engine mock.
import { priceEntryFor } from '../../data/mockPricing.js';

export type GroupKey = 'barn' | 'hydration';

export interface SubscriptionLine {
  name: string;
  qty: number;
  size: string;
  square: 'GX' | 'OM' | 'HM';
  squareVariant?: 'bone';
}

export interface SubscriptionGroup {
  key: GroupKey;
  label: string;
  productsSummary: string;
  summaryLabel: string;
  nextMonth: string;
  nextDay: string;
  nextOrderDate: string;
  frequency: string;
  addressLabel: string;
  address: string;
  cardLast4: string;
  cardExpiry: string;
  cardBrand: string;
  lines: SubscriptionLine[];
}

function sumGroup(lines: SubscriptionLine[]): number {
  return lines.reduce((sum, line) => {
    const entry = priceEntryFor(line.name);
    if (!entry) return sum;
    return sum + entry.subscription * line.qty;
  }, 0);
}

export const BARN_GROUP: SubscriptionGroup = {
  key: 'barn',
  label: 'Barn supplements',
  productsSummary: 'Gut X + Osteo-MAX',
  summaryLabel: 'Gut X + Osteo-MAX',
  nextMonth: 'OCT',
  nextDay: '15',
  nextOrderDate: 'October 15, 2026',
  frequency: 'Every 60 days',
  addressLabel: 'Barn address',
  address: 'Cedar Creek barn · Nashville, TN · Example address',
  cardLast4: '4242',
  cardExpiry: 'Expires 08/2028 · Example',
  cardBrand: 'VISA',
  lines: [
    { name: 'Gut X', qty: 2, size: '1 gallon', square: 'GX' },
    { name: 'Osteo-MAX', qty: 1, size: '1 gallon', square: 'OM', squareVariant: 'bone' },
  ],
};

export const HYDRATION_GROUP: SubscriptionGroup = {
  key: 'hydration',
  label: 'Hydration',
  productsSummary: 'Hydra-MAX',
  summaryLabel: 'Hydra-MAX',
  nextMonth: 'OCT',
  nextDay: '28',
  nextOrderDate: 'October 28, 2026',
  frequency: 'Every 30 days',
  addressLabel: 'Home address',
  address: 'Home · Nashville, TN · Example address',
  cardLast4: '4444',
  cardExpiry: 'Expires 11/2029 · Example',
  cardBrand: 'MC',
  lines: [{ name: 'Hydra-MAX', qty: 1, size: '1 gallon', square: 'HM' }],
};

export const GROUPS: Record<GroupKey, SubscriptionGroup> = {
  barn: BARN_GROUP,
  hydration: HYDRATION_GROUP,
};

export function groupTotal(group: SubscriptionGroup): number {
  return sumGroup(group.lines);
}

export const DIALOG_EXAMPLES: Record<string, { title: string; body: string }> = {
  date: {
    title: 'Change next order date',
    body: 'Choose the date Sarah requested. The current date stays in place until the change is confirmed.',
  },
  frequency: {
    title: 'Change delivery frequency',
    body: "Choose from the day intervals available on Sarah's Recharge plan.",
  },
  products: {
    title: 'Edit subscription products',
    body: 'Review product quantities and the revised recurring price with Sarah. Changes apply to this selected subscription.',
  },
  skip: {
    title: 'Skip next order',
    body: 'Review which order will be skipped and the resulting next order date before confirming.',
  },
  address: {
    title: 'Subscription shipping address',
    body: 'Choose or edit the shipping address for this subscription. Do not silently change other saved addresses or orders.',
  },
  payment: {
    title: 'Update payment method',
    body: "Use the payment provider's secure flow. Full card details are not entered in this workspace.",
  },
  pause: {
    title: 'Pause subscription',
    body: 'Review the requested pause and the provider-supported resume options before confirming.',
  },
  cancel: {
    title: 'Cancel subscription',
    body: "Review the customer's request and the status of any already-processing order before confirming.",
  },
  coverage: {
    title: 'Review Colic Coverage',
    body: 'Show the current enrollment and verified impact of the proposed subscription change.',
  },
};
